import * as React from "react";
import { classNames } from "@/lib/util";
import "./Tabs.scss";

export type TabConfig<T> = {
    id: T;
    title: React.ReactNode;
    content: React.ReactNode;
};

type TabHeaderProps<T> = {
    title: React.ReactNode;
    id: T;
    is_active: boolean;
    onChangeActiveTab: (id: T) => (e: React.MouseEvent<HTMLDivElement>) => void;
};

class TabHeader<T> extends React.Component<TabHeaderProps<T>> {
    render() {
        return (
            <div
                className={classNames("ql-tab", { active: this.props.is_active })}
                onClick={this.props.onChangeActiveTab(this.props.id)}
            >
                {this.props.title}
            </div>
        );
    }
}

type TabContentProps = {
    content: React.ReactNode;
    is_visible: boolean;
};

class TabContent extends React.Component<TabContentProps> {
    render() {
        return <div className={classNames("content", { visible: this.props.is_visible })}>{this.props.content}</div>;
    }
}

type Props<T> = {
    data: TabConfig<T>[];
    active_tab: T | null;
    onChangeActiveTab?: (id: T) => (e: React.MouseEvent<HTMLDivElement>) => void;
};

type State<T> = {
    active_tab: T | null;
};

export default class Tabs<T extends React.Key> extends React.Component<Props<T>, State<T>> {
    state: State<T> = {
        active_tab: null,
    };

    componentDidUpdate(prev_props: Props<T>) {
        if (this.props.data !== prev_props.data) {
            const tab_exists = this.props.data.some(d => d.id === this.state.active_tab);
            if (!tab_exists) {
                this.setState({
                    active_tab: this.props.data[0].id,
                });
            }
        }
        if (prev_props.active_tab !== this.props.active_tab) {
            this.setState({
                active_tab: this.props.active_tab,
            });
        }
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const tabs_data = this.getTabsData();
        if (!this.state.active_tab && tabs_data) {
            this.setState({
                active_tab: tabs_data[0].id,
            });
        }
    };

    getActiveTab = (): T | null => {
        return this.state.active_tab;
    };

    getTabsData = (): TabConfig<T>[] => {
        return this.props.data;
    };

    onChangeActiveTab = (id: T) => () => {
        this.setState({ active_tab: id });
        if (this.props.onChangeActiveTab) {
            this.props.onChangeActiveTab(id);
        }
    };

    render() {
        const tabs_data = this.getTabsData();
        const active_tab = this.getActiveTab();

        return (
            <div className="ql-tabs" key={active_tab}>
                <div className="ql-tabs-header">
                    {tabs_data.map((t, idx) => (
                        <TabHeader
                            onChangeActiveTab={this.onChangeActiveTab}
                            title={t.title}
                            is_active={t.id === active_tab}
                            id={t.id}
                            key={idx}
                        />
                    ))}
                </div>
                <div className="ql-tabs-content-relative">
                    {tabs_data.map((t, idx) => (
                        <TabContent content={t.content} is_visible={t.id === active_tab} key={idx} />
                    ))}
                </div>
            </div>
        );
    }
}
